import { cn } from "@newxsystems/shared";
import { getComponent } from "../../../../client";
import { WohaCommonContent } from "../../../../modules/content/components/WohaCommonContent/WohaCommonContent";
import { WohaMedia } from "../../../../modules/content/components/WohaMedia/WohaMedia";
import { WohaRichText } from "../../../../modules/content/components/WohaRichText/WohaRichText";
import { WohaSection } from "../../../../modules/content/components/WohaSection/WohaSection";
import type { ITeamMemberGridBlockProps } from "./TeamMemberGridBlock.props";

const columns = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
} as const;

export const TeamMemberGridBlock = ({
  alignment,
  anchorId,
  buttons,
  columnCount,
  description,
  eyebrow,
  heading,
  items,
  parentClass,
  spacing,
}: ITeamMemberGridBlockProps) => {
  if (!items?.length) return null;
  const Link = getComponent("Link");
  const effectiveColumns = Math.min(columnCount || 3, items.length) as 1 | 2 | 3 | 4;

  return (
    <WohaSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-10 md:gap-12">
        <WohaCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
        />
        <div className={cn("grid gap-x-6 gap-y-12", columns[effectiveColumns])}>
          {items.map((member, index) => (
            <article className="group flex h-full flex-col" key={index}>
              <WohaMedia
                aspectRatio="3:4"
                className="rounded-2xl border border-ash bg-paper-mist"
                imageClassName="transition-transform duration-500 ease-out group-hover:scale-[1.015]"
                media={member.portrait.image}
                sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
              />
              <div className="flex flex-1 flex-col pt-5">
                <h3 className="font-satoshi text-2xl font-medium leading-tight text-charcoal">
                  {member.name}
                </h3>
                {member.role ? (
                  <p className="mt-1 text-sm font-medium text-steel">{member.role}</p>
                ) : null}
                {member.biography ? (
                  <WohaRichText
                    className="mt-4 text-base leading-7 text-graphite"
                    content={member.biography}
                  />
                ) : null}
                {member.links?.length ? (
                  <div className="mt-auto pt-5">
                    <div className="flex flex-wrap gap-x-4 gap-y-1 border-t border-ash pt-3">
                      {member.links.map((link, linkIndex) => (
                        <Link
                          aria-label={link.accessibilityLabel || undefined}
                          className="inline-flex min-h-11 items-center text-sm font-medium text-charcoal underline decoration-smoke underline-offset-4 transition-colors hover:text-electric-blue focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-electric-blue"
                          href={link.href}
                          key={linkIndex}
                          rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                          target={link.openInNewTab ? "_blank" : undefined}
                        >
                          {link.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      </div>
    </WohaSection>
  );
};

TeamMemberGridBlock.displayName = "Block::TeamMemberGridBlock";
